import React from 'react';
import PropTypes from 'prop-types';
import { format, isValid } from 'date-fns';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * DatePicker component with semantic styling and configuration support
 * Supports date formatting and validation
 */
const DatePicker = ({ 
  id, 
  name, 
  label, 
  value, 
  onChange, 
  placeholder, 
  required = false,
  disabled = false,
  helpText,
  className = '',
  ...props
}) => {
  const colors = useColors();
  const semantic = colors.semantic;
  const { replace } = useTextReplacement();

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  // Add validation to ensure the date is valid before formatting
  const formattedValue = (() => {
    if (!value) return '';
    
    try {
      const dateObj = new Date(value);
      return isValid(dateObj) ? format(dateObj, 'yyyy-MM-dd') : '';
    } catch (e) {
      console.warn('Invalid date value:', value);
      return '';
    }
  })();

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label htmlFor={id} className={`block text-sm font-medium ${semantic.formLabel} mb-1`}>
          {replace(label)}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        type="date"
        id={id}
        name={name}
        value={formattedValue}
        onChange={handleChange}
        placeholder={placeholder ? replace(placeholder) : undefined}
        disabled={disabled}
        required={required}
        className={`w-full px-3 py-2 ${semantic.formBg} ${semantic.formBorder} border rounded-md ${semantic.formText} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
        {...props}
      />
      {helpText && (
        <p className={`mt-1 text-xs ${semantic.formLabel}`}>
          {replace(helpText)}
        </p>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helpText: PropTypes.string,
  className: PropTypes.string
};

export default DatePicker;

